import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff, MdLogin } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${props => props.theme.primary} 0%, ${props => props.theme.primaryDark} 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 50px 40px;
  max-width: 450px;
  width: 100%;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const LogoIcon = styled.div`
  font-size: 64px;
  color: ${props => props.theme.primary};
  margin-bottom: 10px;
`;

const Title = styled.h1`
  color: ${props => props.theme.text};
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.95rem;
  text-align: center;
  margin-bottom: 40px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.text};
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  color: ${props => props.theme.textSecondary};
  font-size: 1.2rem;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 45px 14px 45px;
  border: 2px solid ${props => props.error ? '#e74c3c' : '#e0e0e0'};
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};

  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#e74c3c' : props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.error ? 'rgba(231, 76, 60, 0.1)' : `${props.theme.primary}20`};
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 15px;
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.primary};
  }

  &:focus {
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px 15px;
  border-radius: 8px;
  font-size: 0.9rem;
  border-left: 4px solid #e74c3c;
  margin-bottom: 15px;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, ${props => props.theme.primary}, ${props => props.theme.primaryDark});
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  box-shadow: 0 4px 15px ${props => props.theme.primary}40;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${props => props.theme.primary}60;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoBox = styled.div`
  background: ${props => props.theme.primaryLight}20;
  border: 1px solid ${props => props.theme.primary}40;
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
`;

const InfoTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.primary};
  margin-bottom: 8px;
  font-size: 0.9rem;
`;

const InfoText = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  line-height: 1.5;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 30px;
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validaciones básicas
    if (!email || !password) {
      setError('Por favor, completa todos los campos');
      return;
    }

    if (!email.includes('@')) {
      setError('Por favor, ingresa un email válido');
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || 'Credenciales incorrectas. Por favor, intenta nuevamente.');
      }
    } catch (err) {
      setError('Error al iniciar sesión. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <LogoIcon>💳</LogoIcon>
          <Title>Payment Manager</Title>
          <Subtitle>Inicia sesión en tu cuenta</Subtitle>
        </Logo>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Correo Electrónico</Label>
            <InputWrapper>
              <InputIcon>
                <MdEmail />
              </InputIcon>
              <Input
                type="email"
                placeholder="ejemplo@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={error && !email}
                autoComplete="email"
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <Label>Contraseña</Label>
            <InputWrapper>
              <InputIcon>
                <MdLock />
              </InputIcon>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={error && !password}
                autoComplete="current-password"
              />
              <TogglePasswordButton
                type="button"
                onClick={togglePasswordVisibility}
                tabIndex="-1"
              >
                {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </TogglePasswordButton>
            </InputWrapper>
          </FormGroup>

          <LoginButton type="submit" disabled={loading}>
            {loading ? (
              <>Iniciando sesión...</>
            ) : (
              <>
                <MdLogin />
                Iniciar Sesión
              </>
            )}
          </LoginButton>
        </Form>

        <InfoBox>
          <InfoTitle>🔐 Credenciales de prueba:</InfoTitle>
          <InfoText>
            <strong>Email:</strong> admin@paymentmanager.com<br />
            <strong>Contraseña:</strong> admin123
          </InfoText>
        </InfoBox>

        <Footer>
          © {new Date().getFullYear()} Payment Manager. Todos los derechos reservados.
        </Footer>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
